import { useCallback, useEffect, useState } from 'react';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import {
    CASH_BOOKING_REFUSAL_KEYS,
    type CashBookingReadiness,
    cashBookingRefusalKey,
} from '../domain/cashBookingRefusal';
import useRequest from './useRequest';

interface CashBookingReadinessOptions {
    /**
     * Check as soon as the page knows its cash point, for a page whose form books money: the
     * operator sees the refusal before filling the form in rather than when saving it.
     */
    checkOnMount?: boolean;
}

/**
 * Whether this workstation can book money now - a cash register with an open cash session - as
 * the booking itself will decide (`GET /cash-drawer/booking-readiness`).
 *
 * `ensureCanBook` asks the server and resolves to the i18n key of the refusal, or `null` when the
 * booking can go ahead; a button that starts a contract, a payment, a sale or a disposition calls
 * it before opening the form. `refusalKey` is the last answer, for a banner on the page. A failed
 * check lets the operator go on: the booking still refuses at the end, and a network hiccup must
 * not lock the till.
 */
export default function useCashBookingReadiness({
    checkOnMount = false,
}: CashBookingReadinessOptions = {}) {
    const { cashPointId } = useCashDrawer();
    const request = useRequest();
    const [refusalKey, setRefusalKey] = useState<string | null>(null);

    const fetchRefusalKey = useCallback(async (): Promise<string | null> => {
        if (cashPointId === null) {
            return CASH_BOOKING_REFUSAL_KEYS.NO_CASH_POINT;
        }
        try {
            const readiness = await request<CashBookingReadiness>({
                path: '/cash-drawer/booking-readiness',
                params: { cashPointId },
            });
            return cashBookingRefusalKey(readiness);
        } catch (error) {
            console.error('Cash booking readiness check failed:', error);
            return null;
        }
    }, [cashPointId, request]);

    useEffect(() => {
        // Until App has looked the station up the cash point is unknown, not missing; a click
        // in the meantime still gets the refusal from `ensureCanBook`.
        if (!checkOnMount || cashPointId === null) {
            return;
        }
        let cancelled = false;
        void fetchRefusalKey().then(key => {
            if (!cancelled) setRefusalKey(key);
        });
        return () => {
            cancelled = true;
        };
    }, [checkOnMount, cashPointId, fetchRefusalKey]);

    const ensureCanBook = useCallback(async (): Promise<string | null> => {
        const key = await fetchRefusalKey();
        setRefusalKey(key);
        return key;
    }, [fetchRefusalKey]);

    return { refusalKey, ensureCanBook };
}
